import { Content, ProjectItem } from '../types';

const LAUNCH_EVENT = 'simulator:launch';

// Lets any section (Experience tags, etc.) ask the Projects simulator to build & run an app.
export const requestLaunch = (appStoreId: string) => {
  document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  window.dispatchEvent(new CustomEvent<string>(LAUNCH_EVENT, { detail: appStoreId }));
};

export const onLaunchRequest = (handler: (appStoreId: string) => void) => {
  const listener = (event: Event) => handler((event as CustomEvent<string>).detail);
  window.addEventListener(LAUNCH_EVENT, listener);
  return () => window.removeEventListener(LAUNCH_EVENT, listener);
};

// "A Spor" in projects and "ASPOR" in experience should resolve to the same app.
export const normalizeName = (name: string) =>
  name
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9]/g, '');

export const findProject = (projects: ProjectItem[], name: string) =>
  projects.find(project => normalizeName(project.name) === normalizeName(name));

export interface ProjectGroup {
  company: string;
  period?: string;
  projects: ProjectItem[];
}

// Groups App Store projects under the company where they were built, based on the experience data.
export const groupProjectsByCompany = (content: Content): ProjectGroup[] => {
  const groups: ProjectGroup[] = content.experience.items.map(item => ({
    company: item.company,
    period: item.period,
    projects: content.projects.items.filter(project =>
      item.projects.some(name => normalizeName(name) === normalizeName(project.name))
    ),
  }));

  const grouped = new Set(groups.flatMap(group => group.projects));
  const rest = content.projects.items.filter(project => !grouped.has(project));
  if (rest.length) {
    groups.push({ company: content.projects.simulator.other, projects: rest });
  }

  return groups.filter(group => group.projects.length > 0);
};
